import {
  firestore,
  collection,
  writeBatch,
  serverTimestamp,
  doc,
  query,
  where,
  onSnapshot,
  getDocs,
  storage,
  ref,
  getDownloadURL,
  uploadBytes,
  arrayUnion,
  updateDoc,
  deleteDoc,
  orderBy,
  increment,
  limit,
} from '@/firebase/init'
import { mapActivityData } from '@/components/activity/activityTypes'
import moment from 'moment'

export class ActivityService {
  constructor() {
    this.unsubscribeCallbacks = new Map()
  }

  // Upload file to Firebase Storage
  async uploadFile(file, folder = 'attachments') {
    try {
      const storageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`)
      const snapshot = await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(snapshot.ref)
      return {
        name: file.name,
        type: file.type,
        size: file.size,
        url: downloadURL,
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      throw error
    }
  }

  // Pin or unpin an activity (single pin mode)
  async togglePinActivity(activityId, isPinned, user) {
    try {
      const batch = writeBatch(firestore)
      
      if (isPinned) {
        // If pinning, first unpin any existing pinned activity
        const existingPinnedQuery = query(
          collection(firestore, 'activities'),
          where('isPinned', '==', true)
        )
        
        const existingPinnedSnapshot = await getDocs(existingPinnedQuery)
        if (!existingPinnedSnapshot.empty) {
          // Unpin the existing pinned activity
          const existingPinnedDoc = existingPinnedSnapshot.docs[0]
          batch.update(existingPinnedDoc.ref, {
            isPinned: false,
            pinnedBy: null,
            updated: serverTimestamp()
          })
        }
        
        // Pin the new activity
        const activityRef = doc(firestore, 'activities', activityId)
        batch.update(activityRef, {
          isPinned: true,
          pinnedBy: {
            name: user.name,
            uid: user.uid,
            date: serverTimestamp()
          },
          updated: serverTimestamp()
        })
      } else {
        // If unpinning, just unpin the specific activity
        const activityRef = doc(firestore, 'activities', activityId)
        batch.update(activityRef, {
          isPinned: false,
          pinnedBy: null,
          updated: serverTimestamp()
        })
      }
      
      await batch.commit()
      return true
    } catch (error) {
      console.error('Error toggling pin status:', error)
      throw error
    }
  }

  // Pin an activity
  async pinActivity(activityId, user) {
    return await this.togglePinActivity(activityId, true, user)
  }

  // Unpin an activity
  async unpinActivity(activityId, user) {
    return await this.togglePinActivity(activityId, false, user)
  }

  // Create a new activity
  async createActivity(activityData, options = {}) {
    try {
      const batch = writeBatch(firestore)
      const activityRef = doc(collection(firestore, 'activities'))

      let uploadedFiles = []
      if (activityData.attachments?.length) {
        for (const attachment of activityData.attachments) {
          // Check if it's already an uploaded file (has 'url' property) or a File object
          if (attachment.url) {
            // Already uploaded, use as-is
            uploadedFiles.push(attachment)
          } else {
            // File object, need to upload
            const uploadedFile = await this.uploadFile(attachment)
            uploadedFiles.push(uploadedFile)
          }
        }
      }

      // Remove any undefined values to prevent Firestore errors
      const cleanActivityData = JSON.parse(JSON.stringify(activityData, (key, value) => 
        value === undefined ? null : value
      ));
      
      const finalActivityData = {
        ...cleanActivityData,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        attachments: uploadedFiles,
      }

      batch.set(activityRef, finalActivityData)

      // Optional calendar event update
      if (options.updateCalendarEvent && options.eventId && options.accountId) {
        const eventRef = doc(firestore, "calendar", options.accountId, "events", options.eventId)
        batch.update(eventRef, {
          activity: arrayUnion(finalActivityData),
          updated: serverTimestamp(),
          lastUpdated: {
            name: activityData.user.name,
            id: activityData.user.uid,
            date: serverTimestamp(),
          },
        })
      }

      // Handle mentions and notifications
      if (activityData.mentions?.length) {
        for (const user of activityData.mentions) {
          const userUid = user.uid || user.value;
          
          // Skip creating notification if user UID is not available
          if (!userUid) {
            console.warn('Skipping notification for user without UID:', user);
            continue;
          }
          
          // Build notification message with context
          let notificationMessage = `${activityData.user.name.first} ${activityData.user.name.last} mentioned you`;
          
          // Add context about what was mentioned
          if (activityData.relatedTo) {
            // Handle different name field formats
            let resourceName = null;
            if (activityData.relatedTo.name) {
              if (typeof activityData.relatedTo.name === 'string') {
                // Check if the string contains [object Object] and try to extract order number from relatedID
                if (activityData.relatedTo.name.includes('[object Object]')) {
                  // Try to get order number from the activity data or relatedID
                  if (activityData.changeData?.orderNumber?.internal) {
                    const orderNum = String(activityData.changeData.orderNumber.internal).toUpperCase();
                    resourceName = `Order #${orderNum}`;
                  } else if (activityData.changeData?.orderNumber) {
                    const orderNum = String(activityData.changeData.orderNumber).toUpperCase();
                    resourceName = `Order #${orderNum}`;
                  } else {
                    const fallbackId = String(activityData.relatedID || activityData.relatedTo.id || 'UNKNOWN').toUpperCase();
                    resourceName = `Order #${fallbackId}`;
                  }
                } else {
                  resourceName = activityData.relatedTo.name;
                }
              } else if (activityData.relatedTo.name.internal) {
                // Handle case where name is an object with internal property (like orderNumber)
                const orderNum = String(activityData.relatedTo.name.internal).toUpperCase();
                resourceName = `Order #${orderNum}`;
              } else if (activityData.relatedTo.name.external) {
                // Handle case where name is an object with external property
                const orderNum = String(activityData.relatedTo.name.external).toUpperCase();
                resourceName = `Order #${orderNum}`;
              }
            }
            
            if (resourceName && resourceName !== 'Activity Note') {
              notificationMessage += ` in ${resourceName}`;
            } else if (activityData.relatedTo.type && activityData.relatedTo.type !== 'activity') {
              notificationMessage += ` in a ${activityData.relatedTo.type}`;
            } else {
              notificationMessage += ' in an activity note';
            }
          } else {
            notificationMessage += ' in an activity note';
          }
          
          const notificationRef = doc(collection(firestore, 'notifications'))
          batch.set(notificationRef, {
            created: serverTimestamp(),
            updated: serverTimestamp(),
            assignedTo: {
              name: user.name,
              uid: userUid,
            },
            createdBy: {
              name: activityData.user.name,
              uid: activityData.user.uid,
            },
            lastUpdated: {
              name: activityData.user.name,
              uid: activityData.user.uid,
            },
            notes: notificationMessage,
            relatedTo: activityData.relatedTo,
            viewed: false,
          });
        }
      }

      await batch.commit()
      return activityRef.id
    } catch (error) {
      console.error('Error creating activity:', error)
      throw error
    }
  }

  /**
   * Helper method to create inventory activity tracking.
   * Centralizes all inventory activity creation for sales order, work order, transfer, and reconcile.
   * Requires exactly one of: order (sales), workOrderData (work order), transferData (transfer), or reconciliationData + brand (reconcile).
   *
   * @param {Object} params - Activity parameters
   * @param {string} params.action - Activity action (e.g. "Inventory reserved", "Transfer received")
   * @param {string} params.item - Item description
   * @param {string} params.skuID - SKU identifier (e.g. locationID_brandID_categoryID_productID)
   * @param {Object} params.itemData - Line item data (name, sku, categoryID, productID, categoryName/category, etc.)
   * @param {Object} params.movement - Movement details (type, from, to, quantity, reason, reasonID)
   * @param {Object} params.snapshot - Before/after inventory snapshots (at least available, reserved, transfer)
   * @param {Object} params.location - Location data (id, name)
   * @param {Object} [params.order] - Order data (required for sales order path)
   * @param {Object} [params.brand] - Brand data (required for reconcile; also used when order/workOrderData/transferData provide it)
   * @param {Object} [params.extraSalesOrderData] - Additional sales order data (for sales order path)
   * @param {Object} [params.reconciliationData] - Reconciliation data (receiptID, reconcileType, notes) for reconcile path
   * @param {Object} [params.workOrderData] - Work order context (productWorkOrderID, mainWorkOrderID, orderNumber, locationID, brand, category, product, qty) for work order path
   * @param {Object} [params.transferData] - Transfer context (transferID, orderNumber, sendingLocation, receivingLocation, brand) for transfer path
   * @param {Object} params.user - User data (name, uid)
   * @returns {Promise<string>} - Activity ID
   */
  async createInventoryActivity({
    action,
    item,
    skuID,
    itemData,
    movement,
    snapshot,
    location,
    order,
    brand,
    extraSalesOrderData = {},
    reconciliationData = null,
    workOrderData = null,
    transferData = null,
    user
  }) {
    if (!user) {
      throw new Error('User data is required for inventory activity tracking')
    }

    const hasOrder = order != null && typeof order === 'object'
    const hasWorkOrder = workOrderData != null && typeof workOrderData === 'object'
    const hasTransfer = transferData != null && typeof transferData === 'object'
    const hasReconcile = reconciliationData != null && typeof reconciliationData === 'object'

    const sourceCount = [hasOrder, hasWorkOrder, hasTransfer, hasReconcile].filter(Boolean).length
    if (sourceCount !== 1) {
      throw new Error(
        'createInventoryActivity requires exactly one of: order (sales), workOrderData (work order), transferData (transfer), or reconciliationData + brand (reconcile)'
      )
    }

    let brandInfo
    let accountInfo = null

    if (hasOrder) {
      brandInfo = order.brand
      accountInfo = order.account || null
    } else if (hasWorkOrder) {
      const b = workOrderData.brand
      brandInfo =
        b && (b.id || b.brandID)
          ? { id: b.id || b.brandID, name: b.name || '' }
          : null
    } else if (hasTransfer) {
      brandInfo = transferData.brand
    } else {
      if (!brand || !brand.id) {
        throw new Error('Brand data is required for reconciliation inventory activity tracking')
      }
      brandInfo = brand
    }

    if (!brandInfo || !brandInfo.id) {
      throw new Error('Brand information is required for inventory activity tracking')
    }

    const changeData = {
      movement,
      sku: {
        locationID: location.id,
        locationName: location.name,
        brandID: brandInfo.id,
        brandName: brandInfo.name,
        categoryID: itemData.categoryID,
        categoryName: itemData.categoryName || itemData.category,
        productID: itemData.productID,
        productName: itemData.name,
        productSKU: itemData.sku,
      },
      snapshot,
    }

    if (hasOrder) {
      changeData.salesOrder = {
        id: extraSalesOrderData.orderID || order.id || (movement?.reasonID ? String(movement.reasonID) : null),
        orderNumber: order.orderNumber?.internal ?? order.orderNumber,
        externalPO: order.orderNumber?.external || null,
        customer: order.account ? { id: order.account.id, name: order.account.name } : null,
        ...extraSalesOrderData,
      }
    } else if (hasWorkOrder) {
      changeData.workOrder = {
        productWorkOrderID: workOrderData.productWorkOrderID || null,
        mainWorkOrderID: workOrderData.mainWorkOrderID || null,
        orderNumber: workOrderData.orderNumber || null,
        locationID: workOrderData.locationID || location.id,
        brand: brandInfo ? { id: brandInfo.id, name: brandInfo.name } : null,
        category: workOrderData.category || null,
        product: workOrderData.product || null,
        qty: workOrderData.qty ?? movement?.quantity,
      }
    } else if (hasTransfer) {
      changeData.transfer = {
        transferID: transferData.transferID || null,
        orderNumber: transferData.orderNumber || null,
        sendingLocation: transferData.sendingLocation || null,
        receivingLocation: transferData.receivingLocation || null,
        brand: brandInfo ? { id: brandInfo.id, name: brandInfo.name } : null,
      }
    } else {
      changeData.reconciliation = {
        receiptID: reconciliationData?.receiptID ?? (movement?.reasonID ? String(movement.reasonID) : null),
        reconcileType: reconciliationData?.reconcileType ?? null,
        notes: reconciliationData?.notes ?? null,
      }
    }

    return await this.createActivity({
      type: "inventory",
      action,
      item,
      relatedTo: "sku",
      relatedID: [skuID],
      user: {
        name: user.name,
        uid: user.uid,
      },
      account: accountInfo ? { id: accountInfo.id, name: accountInfo.name } : null,
      brand: { id: brandInfo.id, name: brandInfo.name },
      changeData,
    })
  }

  // Set up real-time listener for activities
  setupActivityListener(accountId, callback, options = {}) {
    const { 
      includeTypes = null, 
      excludeTypes = null,
      relatedTo = null,
      relatedID = null,
      sortDesc = true 
    } = options

    let q = query(collection(firestore, 'activities'))

    if (includeTypes) {
      q = query(q, where('type', 'in', includeTypes))
    }

    // Add relatedID filter if specified (for filtering by specific items)
    if (relatedID) {
      // Handle both string and array relatedID formats
      if (Array.isArray(relatedID)) {
        // If it's an array, check if it contains any of the values
        q = query(q, where('relatedID', 'array-contains-any', relatedID))
      } else {
        // Legacy support for string relatedID
        q = query(q, where('relatedID', '==', relatedID))
      }
    }

    const unsubscribe = onSnapshot(
      q, 
      (snapshot) => {
        try {
          if (snapshot.empty) {
            callback([])
            return
          }

          const activities = snapshot.docs
            .map((doc) => mapActivityData({ id: doc.id, ...doc.data() }))
            .filter((activity) => {
              if (excludeTypes && excludeTypes.includes(activity.type)) {
                return false
              }
              return true
            })
            .sort((a, b) => {
              // First sort by pin status (pinned items first)
              if (a.isPinned && !b.isPinned) return -1
              if (!a.isPinned && b.isPinned) return 1
              
              // Then sort by date
              const sortMultiplier = sortDesc ? 1 : -1
              return moment(b.date).diff(moment(a.date), 'seconds') * sortMultiplier
            })

          callback(activities)
        } catch (error) {
          // Handle errors in processing snapshot data
          console.error('Error processing activity snapshot:', error)
          callback([])
        }
      },
      (error) => {
        // Error handler: log error and call callback with empty array to prevent hanging
        console.error('Error in activity listener:', error)
        try {
          callback([])
        } catch (callbackError) {
          console.error('Error calling activity callback:', callbackError)
        }
      }
    )

    // Store unsubscribe callback with a more specific key when filtering by relatedID
    const cacheKey = relatedID ? `${accountId}_${relatedID}` : accountId
    this.unsubscribeCallbacks.set(cacheKey, unsubscribe)
    return unsubscribe
  }

  // Cleanup listeners
  cleanup(accountId = null) {
    if (accountId) {
      const unsubscribe = this.unsubscribeCallbacks.get(accountId)
      if (unsubscribe) {
        unsubscribe()
        this.unsubscribeCallbacks.delete(accountId)
      }
    } else {
      // Cleanup all listeners
      this.unsubscribeCallbacks.forEach((unsubscribe) => unsubscribe())
      this.unsubscribeCallbacks.clear()
    }
  }

  // Get activity statistics
  getActivityMetrics(activities) {
    const today = moment().startOf('day')
    const weekStart = moment().subtract(7, 'days').startOf('day')
    const lastWeekStart = moment().subtract(14, 'days').startOf('day')

    const todayActivities = activities.filter((item) =>
      moment(item.date).isSame(today, 'day')
    )

    const weekActivities = activities.filter((item) =>
      moment(item.date).isAfter(weekStart)
    )

    const lastWeekActivities = activities.filter((item) =>
      moment(item.date).isBetween(lastWeekStart, weekStart)
    )

    const tasks = weekActivities.filter((item) => item.type === 'checklist')
    const completedTasks = tasks.filter((item) => item.completed)
    const uniqueUsers = [...new Set(weekActivities.map((item) => item.user))]

    const weeklyTrend = lastWeekActivities.length === 0 
      ? (weekActivities.length > 0 ? 100 : 0)
      : Math.round(((weekActivities.length - lastWeekActivities.length) / lastWeekActivities.length) * 100)

    return {
      today: {
        total: todayActivities.length,
        tasks: todayActivities.filter((item) => item.type === 'checklist').length,
        completed: todayActivities.filter(
          (item) => item.type === 'checklist' && item.completed
        ).length,
      },
      weekly: {
        total: weekActivities.length,
        completionRate: tasks.length
          ? Math.round((completedTasks.length / tasks.length) * 100)
          : 0,
        activeUsers: uniqueUsers.length,
        trend: weeklyTrend,
      },
      lastActivity: activities.length > 0 ? activities[0] : null
    }
  }

  /**
   * Convert legacy ledger entries to activity format for unified display
   * @param {Array} ledgerEntries - Array of legacy ledger entries
   * @param {Object} resourceInfo - Information about the current resource (optional)
   * @returns {Array} - Converted activities in new format
   */
  convertLegacyLedgerToActivities(ledgerEntries, resourceInfo = {}) {
    if (!ledgerEntries || !Array.isArray(ledgerEntries)) {
      return [];
    }

    return ledgerEntries.map(entry => {
      // Extract user information from the action HTML
      const userMatch = entry.action?.match(/<span class="text-capitalize">([^<]+)<\/span>/);
      const userName = userMatch ? userMatch[1] : 'System User';
      
      // Extract the actual action text (remove HTML tags)
      let actionText = entry.action || '';
      
      // Remove HTML tags but preserve line breaks
      actionText = actionText
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .trim();
      
      // If we have user info in the action, remove it from the text
      if (userMatch) {
        actionText = actionText.replace(userName, '').replace(/\s+/, ' ').trim();
      }

      return {
        id: entry.id,
        type: 'legacy ledger',
        item: actionText || 'Legacy activity',
        date: entry.date?.toDate?.() || entry.date || new Date(),
        user: {
          name: { 
            first: userName.split(' ')[0] || 'System', 
            last: userName.split(' ').slice(1).join(' ') || 'User' 
          },
          uid: entry.userId || 'legacy-user'
        },
        relatedID: entry.relatedID || entry.salesOrderId || resourceInfo.id,
        relatedTo: {
          id: entry.relatedID || entry.salesOrderId || resourceInfo.id,
          resource: resourceInfo.resource || 'unknown',
          name: resourceInfo.name || 'Unknown Resource',
          type: resourceInfo.type || 'unknown'
        },
        // Legacy specific fields
        legacyData: {
          originalAction: entry.action,
          originalDate: entry.date,
          originalUser: userName,
          hasHtmlContent: entry.action?.includes('<') || false
        },
        // Mark as legacy for special styling
        isLegacy: true
      };
    });
  }

  /**
   * Get combined activities from both new activity system and legacy ledger
   * @param {Array} activities - New activities
   * @param {Array} ledgerEntries - Legacy ledger entries
   * @param {Object} resourceInfo - Information about the current resource (optional)
   * @returns {Array} - Combined and sorted activities
   */
  getCombinedActivities(activities = [], ledgerEntries = [], resourceInfo = {}) {
    const convertedLegacy = this.convertLegacyLedgerToActivities(ledgerEntries, resourceInfo);
    const combined = [...activities, ...convertedLegacy];
    
    // Sort by date (newest first)
    return combined.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });
  }

  // ===== REPLY METHODS =====

  /**
   * Create a reply to an activity
   * @param {string} activityId - ID of the parent activity
   * @param {Object} replyData - Reply data
   * @returns {Promise<string>} - Reply ID
   */
  async createReply(activityId, replyData) {
    try {
      const batch = writeBatch(firestore)
      const replyRef = doc(collection(firestore, 'activities', activityId, 'replies'))

      // Clean reply data
      const cleanReplyData = JSON.parse(JSON.stringify(replyData, (key, value) => 
        value === undefined ? null : value
      ))

      const finalReplyData = {
        ...cleanReplyData,
        created: serverTimestamp(),
        updated: serverTimestamp(),
      }

      batch.set(replyRef, finalReplyData)

      // Update parent activity's reply count and last reply date
      const activityRef = doc(firestore, 'activities', activityId)
      batch.update(activityRef, {
        replyCount: increment(1),
        lastReplyDate: serverTimestamp(),
        updated: serverTimestamp()
      })

      await batch.commit()
      return replyRef.id
    } catch (error) {
      console.error('Error creating reply:', error)
      throw error
    }
  }

  /**
   * Get replies for a specific activity
   * @param {string} activityId - ID of the parent activity
   * @returns {Promise<Array>} - Array of replies
   */
  async getReplies(activityId) {
    try {
      const repliesRef = collection(firestore, 'activities', activityId, 'replies')
      const q = query(repliesRef, orderBy('created', 'asc'))
      const snapshot = await getDocs(q)
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().created?.toDate?.() || new Date()
      }))
    } catch (error) {
      console.error('Error fetching replies:', error)
      throw error
    }
  }

  /**
   * Set up real-time listener for replies
   * @param {string} activityId - ID of the parent activity
   * @param {Function} callback - Callback function for updates
   * @returns {Function} - Unsubscribe function
   */
  setupReplyListener(activityId, callback) {
    const repliesRef = collection(firestore, 'activities', activityId, 'replies')
    const q = query(repliesRef, orderBy('created', 'asc'))
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const replies = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().created?.toDate?.() || new Date()
      }))
      callback(replies)
    })

    // Store unsubscribe callback
    const cacheKey = `replies_${activityId}`
    this.unsubscribeCallbacks.set(cacheKey, unsubscribe)
    return unsubscribe
  }

  /**
   * Set up real-time listener for inventory report (All Activities / By SKU).
   * @param {Object} opts - { brandId, locationId?, limit?, windowHours?, startDate?, endDate? }
   * @param {Function} callback - (dataOrError) => void; dataOrError is array of activities or error
   * @returns {Function} - Unsubscribe function
   */
  setupInventoryReportListener(opts, callback) {
    const {
      brandId,
      locationId,
      limit: limitCount = 50,
      windowHours = 24,
      startDate: rangeStart,
      endDate: rangeEnd,
    } = opts || {}
    if (!brandId) {
      callback([])
      return () => {}
    }
    const startDate =
      rangeStart && rangeEnd
        ? (rangeStart instanceof Date ? rangeStart : new Date(rangeStart))
        : moment().subtract(windowHours, 'hours').toDate()
    const endDate =
      rangeStart && rangeEnd
        ? (rangeEnd instanceof Date ? rangeEnd : new Date(rangeEnd))
        : moment().toDate()
    const activitiesRef = collection(firestore, 'activities')
    const constraints = [
      where('type', '==', 'inventory'),
      where('brand.id', '==', brandId),
      where('created', '>=', startDate),
      where('created', '<=', endDate),
      orderBy('created', 'desc'),
      limit(limitCount),
    ]
    if (locationId) {
      constraints.splice(4, 0, where('changeData.sku.locationID', '==', locationId))
    }
    const q = query(activitiesRef, ...constraints)

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        try {
          if (snapshot.empty) {
            callback([])
            return
          }
          const activities = snapshot.docs
            .map((docSnap) => {
              try {
                return mapActivityData({ id: docSnap.id, ...docSnap.data() })
              } catch (err) {
                console.error('Error mapping activity:', err)
                return null
              }
            })
            .filter(
              (a) =>
                a &&
                a.type === 'inventory' &&
                (a.relatedTo === 'sku' || a.relatedTo?.resource === 'sku')
            )
            .sort((a, b) => {
              const tA = a.date && (a.date.toDate ? a.date.toDate() : new Date(a.date))
              const tB = b.date && (b.date.toDate ? b.date.toDate() : new Date(b.date))
              return tB - tA
            })
          callback(activities)
        } catch (error) {
          console.error('Error processing inventory report snapshot:', error)
          callback([])
        }
      },
      (error) => {
        console.error('Inventory report listener error:', error)
        try {
          callback([])
        } catch (e) {
          console.error('Error calling inventory report callback:', e)
        }
      }
    )
    const mode =
      rangeStart && rangeEnd
        ? `table_${limitCount}_${+rangeStart}_${+rangeEnd}`
        : `ledger_${limitCount}_${windowHours}`
    const cacheKey = `inventoryReport_${brandId}_${locationId || 'all'}_${mode}`
    this.unsubscribeCallbacks.set(cacheKey, unsubscribe)
    return unsubscribe
  }

  /**
   * Update a reply
   * @param {string} activityId - ID of the parent activity
   * @param {string} replyId - ID of the reply to update
   * @param {Object} updateData - Data to update
   * @returns {Promise<void>}
   */
  async updateReply(activityId, replyId, updateData) {
    try {
      const replyRef = doc(firestore, 'activities', activityId, 'replies', replyId)
      await updateDoc(replyRef, {
        ...updateData,
        updated: serverTimestamp(),
        isEdited: true,
        editedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error updating reply:', error)
      throw error
    }
  }

  /**
   * Delete a reply
   * @param {string} activityId - ID of the parent activity
   * @param {string} replyId - ID of the reply to delete
   * @returns {Promise<void>}
   */
  async deleteReply(activityId, replyId) {
    try {
      const batch = writeBatch(firestore)
      const replyRef = doc(firestore, 'activities', activityId, 'replies', replyId)
      
      // Delete the reply
      batch.delete(replyRef)
      
      // Get remaining replies to find the new last reply date
      const repliesRef = collection(firestore, 'activities', activityId, 'replies')
      const q = query(repliesRef, orderBy('created', 'desc'))
      const snapshot = await getDocs(q)
      
      const activityRef = doc(firestore, 'activities', activityId)
      const updateData = {
        replyCount: increment(-1),
        updated: serverTimestamp()
      }
      
      // If there are remaining replies, update lastReplyDate to the most recent one
      if (snapshot.docs.length > 0) {
        const lastReply = snapshot.docs[0]
        updateData.lastReplyDate = lastReply.data().created
      } else {
        // No replies left, remove lastReplyDate
        updateData.lastReplyDate = null
      }
      
      batch.update(activityRef, updateData)
      await batch.commit()
    } catch (error) {
      console.error('Error deleting reply:', error)
      throw error
    }
  }

  // Migration method to add lastReplyDate to existing activities
  async migrateLastReplyDates() {
    try {
      console.log('Starting migration of lastReplyDate fields...')
      const activitiesRef = collection(firestore, 'activities')
      const q = query(activitiesRef, where('replyCount', '>', 0))
      const snapshot = await getDocs(q)
      
      const batch = writeBatch(firestore)
      let batchCount = 0
      
      for (const activityDoc of snapshot.docs) {
        const activityData = activityDoc.data()
        
        // Skip if already has lastReplyDate
        if (activityData.lastReplyDate) continue
        
        // Get the most recent reply
        const repliesRef = collection(firestore, 'activities', activityDoc.id, 'replies')
        const repliesQuery = query(repliesRef, orderBy('created', 'desc'), limit(1))
        const repliesSnapshot = await getDocs(repliesQuery)
        
        if (repliesSnapshot.docs.length > 0) {
          const lastReply = repliesSnapshot.docs[0]
          const activityRef = doc(firestore, 'activities', activityDoc.id)
          batch.update(activityRef, {
            lastReplyDate: lastReply.data().created
          })
          
          batchCount++
          if (batchCount >= 500) { // Firestore batch limit
            await batch.commit()
            batchCount = 0
          }
        }
      }
      
      if (batchCount > 0) {
        await batch.commit()
      }
      
      console.log('Migration completed successfully')
    } catch (error) {
      console.error('Error during migration:', error)
      throw error
    }
  }
}

// Export singleton instance
export const activityService = new ActivityService() 

// Export createActivity function directly for convenience
export const createActivity = async (activityData, options = {}) => {
  return await activityService.createActivity(activityData, options)
}

// Export createInventoryActivity function directly for convenience
export const createInventoryActivity = async (params) => {
  return await activityService.createInventoryActivity(params)
} 